const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

export const getMediaTranscodeOutputId = (mediaInfo = {}) => firstDefined(
  mediaInfo.mediaTranscodeOutputId,
  mediaInfo.media_transcode_output_id,
  mediaInfo.MEDIA_TRANSCODE_OUTPUT_ID
);

export const isMediaTranscodeVodInfo = (mediaInfo = {}) => {
  const originType = String(firstDefined(
    mediaInfo.originType,
    mediaInfo.origin_type,
    mediaInfo.ORIGIN_TYPE,
    "LEGACY_VOD"
  )).toUpperCase();
  return originType === "MEDIA_TRANSCODE" || Boolean(getMediaTranscodeOutputId(mediaInfo));
};

export const parseVideoTimeSeconds = (videoTime) => {
  if (!videoTime || typeof videoTime !== "string") return 0;
  const [hour = "0", min = "0", sec = "0"] = videoTime.split(":");
  return (Number(hour) * 60 * 60) + (Number(min) * 60) + Number(sec);
};

const normalizePlaybackSourceType = (value) => {
  const type = String(value || "").toLowerCase();
  if (type === "hls" || type === "application/vnd.apple.mpegurl" || type === "vnd.apple.mpegurl") {
    return "hls";
  }
  if (type === "mp4" || type === "video/mp4") {
    return "mp4";
  }
  return "";
};

const resolvePlaybackPathname = (playbackUrl = "") => {
  const rawUrl = String(playbackUrl || "");
  if (!rawUrl) return "";
  try {
    const baseUrl = typeof window !== "undefined" && window.location
      ? window.location.origin
      : "http://localhost";
    return decodeURIComponent(new URL(rawUrl, baseUrl).pathname || "").toLowerCase();
  } catch (e) {
    return decodeURIComponent(rawUrl.split("#")[0].split("?")[0]).toLowerCase();
  }
};

export const getOndemandPlaybackSourceType = (mediaInfo = {}, playbackUrl = "", options = {}) => {
  const explicitType = normalizePlaybackSourceType(firstDefined(
    mediaInfo.sourceType,
    mediaInfo.source_type,
    mediaInfo.playbackType,
    mediaInfo.playback_type,
    mediaInfo.mimeType,
    mediaInfo.mime_type
  ));
  if (explicitType) {
    return explicitType;
  }

  const pathOnly = resolvePlaybackPathname(playbackUrl || mediaInfo.publicUrl || mediaInfo.signedUrl || "");
  if (options.viaTranscodePreview || pathOnly.includes("/preview-hls") || pathOnly.endsWith(".m3u8")) {
    return "hls";
  }
  if (pathOnly.endsWith(".mp4")) {
    return "mp4";
  }
  return isMediaTranscodeVodInfo(mediaInfo) ? "hls" : "mp4";
};

export const resolveOndemandPlaybackSource = async (mediaInfo = {}, requestPreviewUrl) => {
  const fallbackUrl = mediaInfo.publicUrl || mediaInfo.signedUrl || "";
  const outputId = getMediaTranscodeOutputId(mediaInfo);
  if (fallbackUrl) {
    return {
      url: fallbackUrl,
      sourceType: getOndemandPlaybackSourceType(mediaInfo, fallbackUrl),
    };
  }
  if (!isMediaTranscodeVodInfo(mediaInfo) || !outputId || typeof requestPreviewUrl !== "function") {
    return {
      url: fallbackUrl,
      sourceType: getOndemandPlaybackSourceType(mediaInfo, fallbackUrl),
    };
  }

  try {
    const res = await requestPreviewUrl(outputId);
    const resolvedUrl = firstDefined(
      res && res.data && res.data.signed_url,
      res && res.data && res.data.signedUrl,
      res && res.signed_url,
      res && res.signedUrl,
      fallbackUrl
    );
    return {
      url: resolvedUrl,
      sourceType: getOndemandPlaybackSourceType(mediaInfo, resolvedUrl, { viaTranscodePreview: true }),
    };
  } catch (e) {
    return {
      url: fallbackUrl,
      sourceType: getOndemandPlaybackSourceType(mediaInfo, fallbackUrl),
    };
  }
};

export const resolveOndemandPlaybackUrl = async (mediaInfo = {}, requestPreviewUrl) => {
  const playbackSource = await resolveOndemandPlaybackSource(mediaInfo, requestPreviewUrl);
  return playbackSource.url;
};
